import { useEffect, useState } from 'react';
import OverlayLoader from '../../../../../components/overlayLoader';
import LoanCardList from '../components/LoanCardList';
import { resellerStatusPerLoanType } from '../utils';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../../services/auth/AuthContext';

import fetchApplicationsService from '../../../../../services/api/fetchApplications';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function UnserviceableApplications({ loanType }) {
    const { t } = useTranslation();

    const { setIsAuthenticated } = useAuth();
    const navigate = useNavigate();

    const resellerStatusValuesUnserviceable = resellerStatusPerLoanType[loanType]['unserviceable_loans'];

    const [searchParams] = useSearchParams();
    const source = searchParams.get('source') ?? 'all';
    const campaign = searchParams.get('campaign') ?? 'all';
    const state = searchParams.get('state') ?? 'all';
    const priority = searchParams.get('priority') ?? 'all';
    const dispositions = searchParams.get('dispositions') ?? 'all';
    const sort = searchParams.get('sort') ?? 'default';
    const rejectionReason = searchParams.get('rejectionReason') ?? 'all';

    const [applications, setApplications] = useState([]);
    const [resellerData, setResellerData] = useState({});
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [cursor, setCursor] = useState(null);

    const fetchLoanApplicationsData = async (isNewSearch = false, isLoading = true) => {
        try {
            setLoading(isLoading);
            const isInitialLoad = isNewSearch || applications.length === 0;
            // The cursor is the backend's, not ours: under a sort other than
            // newest-id-first, the last row's id alone does not identify the page
            // boundary, so the pair it returns has to come back verbatim.
            const pageCursor = isNewSearch ? null : cursor;

            const response = await fetchApplicationsService.getApplicationsForStatusCode(
                "",
                {   loan_type_code: loanType,
                    source: source,
                    campaign: campaign,
                    state: state,
                    priority: priority,
                    dispositions: dispositions,
                    sort: sort,
                    ...(pageCursor && {
                        last_id: pageCursor.last_id,
                        last_sort_value: pageCursor.last_sort_value ?? '',
                    }),
                    limit: 5,
                    current_application_status: 'unserviceable',
                    rejection_reason: rejectionReason,
                },
                setIsAuthenticated,
                navigate
            );

            if (isInitialLoad) {
                setApplications(response.applications || []);
            } else {
                setApplications(prevApplications => [...prevApplications, ...(response.applications || [])]);
            }

            
            setResellerData((prev) => ({
				...prev,
				...response.reseller_data,
			}));
            setHasMore(response.has_more);
            setCursor(response.next_cursor ?? null);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setApplications([]);
        setCursor(null);
        setHasMore(true);
        fetchLoanApplicationsData(true);
    }, [source, campaign, state, priority, dispositions, sort]);

    return (
        <Box
            display={'flex'}
            flexDirection={'column'}
            height={'100%'}
            overflow={'auto'}
        >
            <OverlayLoader
                open={loading}
                text={t('loading.unserviceable') + '...'}
            />

            {applications ? (
                <>
                    <LoanCardList
                        applications={applications}
                        setApplications={setApplications}
                        allStatus={resellerStatusValuesUnserviceable}
                        resellerData={resellerData}
                        loanType={loanType}
                        loadMore={() => fetchLoanApplicationsData(false, false)}
                        hasMore={hasMore}
                        showStepper={false}
                    />
                </>
            ) : (
                <>
                    <Typography>No data available</Typography>
                </>
            )}
        </Box>
    );
}